import api from "./api";

const sp = (schemaName) => (schemaName ? { client: schemaName } : {});

// --- CaptureConfig CRUD ---

export const getAllCaptureConfigs = async (schemaName = null) => {
  try {
    const response = await api.get("/capture-configs/", { params: sp(schemaName) });
    return response.data.results || response.data;
  } catch (error) {
    console.error("Error fetching capture configs:", error);
    throw error;
  }
};

export const getCaptureConfigsForProbe = async (probeId, schemaName = null) => {
  try {
    const params = { ...sp(schemaName), probe: probeId };
    const response = await api.get("/capture-configs/", { params });
    return response.data.results || response.data;
  } catch (error) {
    console.error("Error fetching capture configs for probe:", error);
    throw error;
  }
};

export const getCaptureConfigById = async (id, schemaName = null) => {
  try {
    const response = await api.get(`/capture-configs/${id}/`, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error fetching capture config:", error);
    throw error;
  }
};

export const createCaptureConfig = async (data, schemaName = null) => {
  try {
    const response = await api.post("/capture-configs/", data, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error creating capture config:", error);
    throw error;
  }
};

export const updateCaptureConfig = async (id, data, schemaName = null) => {
  try {
    const response = await api.patch(`/capture-configs/${id}/`, data, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error updating capture config:", error);
    throw error;
  }
};

export const deleteCaptureConfig = async (id, schemaName = null) => {
  try {
    const response = await api.delete(`/capture-configs/${id}/`, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error deleting capture config:", error);
    throw error;
  }
};

export const approveCaptureConfig = async (id, schemaName = null) => {
  try {
    const response = await api.post(`/capture-configs/${id}/approve/`, {}, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error approving capture config:", error);
    throw error;
  }
};

export const denyCaptureConfig = async (id, schemaName = null) => {
  try {
    const response = await api.post(`/capture-configs/${id}/deny/`, {}, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error denying capture config:", error);
    throw error;
  }
};

// --- Files: superuser only ---

export const listCaptureFiles = async (captureId, schemaName = null) => {
  try {
    const response = await api.get(`/capture-configs/${captureId}/files/`, { params: sp(schemaName) });
    return response.data;
  } catch (error) {
    console.error("Error listing capture files:", error);
    throw error;
  }
};

export const buildCaptureDownloadUrl = (captureId, fileId, schemaName = null) => {
  const base = `/capture-configs/${captureId}/download/${fileId}/`;
  if (schemaName) {
    return `${base}?client=${encodeURIComponent(schemaName)}`;
  }
  return base;
};

export const downloadCaptureFile = async (captureId, fileId, schemaName = null) => {
  try {
    const response = await api.get(buildCaptureDownloadUrl(captureId, fileId), {
      responseType: "blob",
    });
    return response;
  } catch (error) {
    console.error("Error downloading capture file:", error);
    throw error;
  }
};

// --- Probe interfaces (grain-backed picker) ---

export const getCaptureProbeInterfaces = async (probeId, schemaName = null) => {
  try {
    const response = await api.get(`/capture-probes/${probeId}/interfaces/`, { params: sp(schemaName) });
    return response.data.interfaces || [];
  } catch (error) {
    console.error("Error fetching probe interfaces:", error);
    throw error;
  }
};

// Probe telemetry: disk totals + systemd service state + computed budget.
// refresh=true forces a live salt round-trip; false reuses the 5-min cache.
export const getProbeTelemetry = async (probeId, { refresh = false, schemaName = null } = {}) => {
  try {
    const params = sp(schemaName);
    if (refresh) params.refresh = 1;
    const response = await api.get(`/capture-probes/${probeId}/telemetry/`, { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching probe telemetry:", error);
    throw error;
  }
};

// Bulk telemetry: single POST that dispatches one salt list-target call for all
// cache-miss probes. Use this anywhere the UI would otherwise fire N per-probe
// GETs (the capture list page's mount hook). Returns
// { results: { <probe_id>: payload }, missing: [ <probe_id>, ... ] }.
// Payload shape for each probe matches the single-probe endpoint, so callers
// can reuse the same rendering paths.
//
// The server caps a single call at MAX_BULK_TELEMETRY_PROBES (currently 100;
// see probe_telemetry_bulk.py) to keep a tampered client from fanning out
// a salt cmd.run across the entire fleet. Tenants with >100 probes need to
// chunk client-side; this helper does it transparently.
const BULK_TELEMETRY_CHUNK_SIZE = 100;

export const getProbeTelemetryBulk = async (probeIds, { schemaName = null } = {}) => {
  if (!Array.isArray(probeIds) || probeIds.length === 0) {
    return { results: {}, missing: [] };
  }
  const params = sp(schemaName);
  const merged = { results: {}, missing: [] };
  for (let i = 0; i < probeIds.length; i += BULK_TELEMETRY_CHUNK_SIZE) {
    const chunk = probeIds.slice(i, i + BULK_TELEMETRY_CHUNK_SIZE);
    try {
      const response = await api.post(
        `/capture-probes/telemetry/bulk/`,
        { probe_ids: chunk },
        { params },
      );
      const data = response.data || {};
      Object.assign(merged.results, data.results || {});
      if (Array.isArray(data.missing)) {
        merged.missing.push(...data.missing);
      }
    } catch (error) {
      console.error("Error fetching bulk probe telemetry chunk:", error);
      throw error;
    }
  }
  return merged;
};

// Ask the probe to restart one of its systemd units. The unit must be one of
// the dumpcap@<pk>.service / capture-offloads@<iface>.service names managed
// by the probe; the backend validates that before dispatching.
export const restartProbeService = async (probeId, unit, { schemaName = null } = {}) => {
  try {
    const response = await api.post(
      `/capture-probes/${probeId}/services/restart/`,
      { unit },
      { params: sp(schemaName) },
    );
    return response.data;
  } catch (error) {
    console.error("Error restarting probe service:", error);
    throw error;
  }
};

// ----- Async download flow -----
// prepareDownload starts an async probe-to-master pcap transfer with optional
// rate limit. Returns {job_id}. Frontend then polls getDownloadJobStatus until
// status === 'done', then GETs the existing download URL with ?job_id=
// to actually receive the file as a blob.

export const prepareDownload = async (captureId, fileId, { rateKbps = null, schemaName = null } = {}) => {
  try {
    const params = sp(schemaName);
    const body = { rate_kbps: rateKbps };
    const response = await api.post(
      `/capture-configs/${captureId}/download/${fileId}/prepare/`,
      body,
      { params },
    );
    return response.data;
  } catch (error) {
    console.error("Error preparing download:", error);
    throw error;
  }
};

export const getDownloadJobStatus = async (jobId, { schemaName = null } = {}) => {
  try {
    const params = sp(schemaName);
    const response = await api.get(`/capture-download-jobs/${jobId}/`, { params });
    return response.data;
  } catch (error) {
    console.error("Error polling download job:", error);
    throw error;
  }
};
